/**
 * The units a drawing's numbers can mean, each with its length in tenths of a millimetre,
 * whole numbers so a conversion multiplies and divides exactly.
 */
export const UNITS = {
  mm: { label: 'Millimeters', tenthsOfMm: 10 },
  cm: { label: 'Centimeters', tenthsOfMm: 100 },
  m: { label: 'Meters', tenthsOfMm: 10000 },
  in: { label: 'Inches', tenthsOfMm: 254 },
  ft: { label: 'Feet', tenthsOfMm: 3048 },
} as const;

export type Unit = keyof typeof UNITS;

/**
 * How lengths are shown. A fractional format rounds to the nearest `1/fraction` inch; a
 * decimal one shows its unit to `precision` places.
 */
export const LENGTH_FORMATS = {
  architectural: { label: 'Architectural', unit: 'in', suffix: '"', fractional: true },
  fractional: { label: 'Fractional inches', unit: 'in', suffix: '"', fractional: true },
  in: { label: 'Decimal inches', unit: 'in', suffix: '"', fractional: false },
  ft: { label: 'Decimal feet', unit: 'ft', suffix: "'", fractional: false },
  mm: { label: 'Millimeters', unit: 'mm', suffix: ' mm', fractional: false },
  cm: { label: 'Centimeters', unit: 'cm', suffix: ' cm', fractional: false },
  m: { label: 'Meters', unit: 'm', suffix: ' m', fractional: false },
} as const satisfies Record<
  string,
  { label: string; unit: Unit; suffix: string; fractional: boolean }
>;

export type LengthFormat = keyof typeof LENGTH_FORMATS;

/** The smallest fractions a fractional format can show, as denominators. */
export const FRACTIONS = [2, 4, 8, 16, 32, 64] as const;

/** The most decimal places a decimal format shows. */
export const MAX_PRECISION = 6;

/** Everything needed to show a length in the drawing's units. */
export interface LengthDisplay {
  /** What the drawing's numbers mean. */
  readonly unit: Unit;
  readonly format: LengthFormat;
  readonly precision: number;
  readonly fraction: number;
  /** Whether a length ends in its unit; architectural always keeps its foot and inch marks. */
  readonly suffix: boolean;
}

export const DEFAULT_DISPLAY: LengthDisplay = {
  unit: 'in',
  format: 'architectural',
  precision: 2,
  fraction: 16,
  suffix: true,
};

export const isUnit = (value: unknown): value is Unit =>
  typeof value === 'string' && Object.hasOwn(UNITS, value);

export const isLengthFormat = (value: unknown): value is LengthFormat =>
  typeof value === 'string' && Object.hasOwn(LENGTH_FORMATS, value);

export const convert = (value: number, from: Unit, to: Unit): number =>
  from === to ? value : (value * UNITS[from].tenthsOfMm) / UNITS[to].tenthsOfMm;

/** `value` to `places` decimals, without trailing zeros or a negative zero. */
export function formatDecimal(value: number, places: number): string {
  const clamped = Math.min(Math.max(Math.round(places), 0), MAX_PRECISION);
  const rounded = Number(value.toFixed(clamped));
  return String(rounded === 0 ? 0 : rounded);
}

/** A length in the drawing's units, as `display` shows it. */
export function formatLength(value: number, display: LengthDisplay): string {
  if (!Number.isFinite(value)) return String(value);
  const format = LENGTH_FORMATS[display.format];
  const shown = convert(value, display.unit, format.unit);
  if (!format.fractional)
    return formatDecimal(shown, display.precision) + (display.suffix ? format.suffix : '');

  const steps = Math.round(Math.abs(shown) * display.fraction);
  const sign = shown < 0 && steps > 0 ? '-' : '';
  const whole = Math.floor(steps / display.fraction);
  const part = fractionText(steps % display.fraction, display.fraction);
  if (display.format === 'fractional') {
    const inches = part === '' ? String(whole) : whole === 0 ? part : `${whole} ${part}`;
    return sign + inches + (display.suffix ? '"' : '');
  }
  const feet = Math.floor(whole / 12);
  const inches = whole % 12;
  const inchText = part === '' ? String(inches) : inches === 0 ? part : `${inches} ${part}`;
  return sign + (feet > 0 ? `${feet}'-${inchText}"` : `${inchText}"`);
}

/** An area in the drawing's units squared, in square feet for architectural, else decimal. */
export function formatArea(value: number, display: LengthDisplay): string {
  if (!Number.isFinite(value)) return String(value);
  const format = LENGTH_FORMATS[display.format];
  const unit: Unit = display.format === 'architectural' ? 'ft' : format.unit;
  const factor = convert(1, display.unit, unit);
  return `${formatDecimal(value * factor * factor, display.precision)} ${unit}²`;
}

/** `numerator/denominator` in lowest terms, or nothing for zero. */
function fractionText(numerator: number, denominator: number): string {
  if (numerator === 0) return '';
  let a = numerator;
  let b = denominator;
  while (b !== 0) [a, b] = [b, a % b];
  return `${numerator / a}/${denominator / a}`;
}
